<template>
    <div class="main-container">
        <v-container fluid class="publish-panel overflow-auto pa-5">
            <h4 class="mb-2">{{ $t('publish_event') }}</h4>
            <p class="tgk-paragraph-text" v-html="$t('publish_staff_infotxt')"></p>

            <ActionBtn v-if="!hidePreview" :href="getUrl(true)" target="tgk_preview" class="mb-5">
                <KpIcon icon-cls="preview" color="white" left></KpIcon>
                <span>{{ $t('preview') }}</span>
            </ActionBtn>

            <!-- info box -->
            <SmallInfoBox :event="record" />

            <v-row class="mt-5" justify="end" style="gap: 10px">
                <ActionBtn :disabled="!draftAllowed" @click="onDraftBtn">
                    <KpIcon icon-cls="pencil" color="white" />
                    <span>{{ $t('draft') }}</span>
                </ActionBtn>
                <ActionBtn v-if="userIsStaff" :disabled="!rejectAllowed" @click="onRejectBtn">
                    <KpIcon icon-cls="rejected" color="white" />
                    <span>{{ $t('reject') }}</span>
                </ActionBtn>
                <ActionBtn :disabled="!publishAllowed" @click="onPublishBtn()">
                    <KpIcon icon-cls="published" color="white" />
                    <span>{{ $t('publish') }}</span>
                </ActionBtn>
                <ActionBtn :disabled="!publishAllowed" @click="onPublishBtn(true)">
                    <KpIcon icon-cls="published" color="white" />
                    <span>{{ $t('publish_redigiert') }}</span>
                </ActionBtn>
            </v-row>

            <v-row justify="end" class="mt-10">
                <v-card v-if="userIsStaff" width="300">
                    <v-card-title>
                        {{ $t('event_quality') }}
                    </v-card-title>
                    <v-card-text>
                        <v-row>
                            <v-col>
                                <LabelField :label="$t('quality')" label-tip="0=nicht bewertet, 1=schlecht - 5=super!">
                                    <v-slider
                                        :value="record.quality"
                                        color="primary"
                                        track-color="primary"
                                        step="1"
                                        min="0"
                                        max="5"
                                        :tick-labels="['0', '1', '2', '3', '4', '5']"
                                        @input="onInput('quality', $event)"
                                    />
                                </LabelField>
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col>
                                <LabelField :label="$t('quality_checked_at')">
                                    <DateTextfield
                                        :clearable="false"
                                        :value="record.quality_checked_at"
                                        :placeholder="$t('select_date')"
                                        hide-details="auto"
                                        @input="onInput('quality_checked_at', $event)"
                                    />
                                </LabelField>
                            </v-col>
                        </v-row>
                    </v-card-text>
                    <v-card-actions>
                        <v-spacer />
                        <ActionBtn @click="$emit('save')">
                            <KpIcon icon-cls="save" color="white" />
                            <span>{{ $t('save') }}</span>
                        </ActionBtn>
                    </v-card-actions>
                </v-card>
            </v-row>
        </v-container>

        <!-- Side panel -->
        <div class="info-panel overflow-auto pa-5">
            <KpAlert v-for="banner in infoboxesPublish" :key="banner.id" type="info" style="max-width: 600px">
                <h4>{{ banner.title }}</h4>
                <p>{{ banner.comment }}</p>
            </KpAlert>
        </div>
        <ConfirmDialog
            v-model="showPublishConfirmDlg"
            :title="$t('publish')"
            :message="$t('publish_sure')"
            @confirm="doPublish"
        />
        <PromptDialog
            v-if="showRejectConfirmDlg"
            v-model="showRejectConfirmDlg"
            :title="$t('reject')"
            :message="$t('reject_event_sure')"
            :response-required="false"
            :confirm-label="$t('reject').toLowerCase()"
            :confirm-alternate-label="$t('reject_with_mail')"
            @confirm="doReject"
            @confirm-alternate="doRejectWithMail"
        />
        <PromptDialog
            v-if="showDraftConfirmDlg"
            v-model="showDraftConfirmDlg"
            :title="$t('draft')"
            :message="$t('draft_sure')"
            :response-required="false"
            :confirm-label="$t('draft')"
            :confirm-alternate-label="$t('draft_with_mail')"
            @confirm="doDraft"
            @confirm-alternate="doDraftWithMail"
        />
        <EventCheckDialog v-model="showReasonDlg" :reasons="eventCheckReasons" @publish="publishAnyway" />
    </div>
</template>

<script>
import { mapActions } from 'vuex';
import { Event } from '@/models/Event';
import { instance as api } from '@/helpers/api';
import { errToStr, formRules } from '@/helpers/tools';
import { globalParam } from '@/helpers/globals';
import { userIsStaff } from '@/helpers/auth';
import GlobalsMixin from '@/mixins/globals';

import SmallInfoBox from '@/components/event/SmallInfoBox.vue';
import LabelField from '../form/LabelField.vue';
import EventCheckDialog from './EventCheckDialog.vue';
import BackRouteMixin from '@/mixins/backroute';
import { commitToast } from '@/plugins/toast';

export default {
    name: 'EventEditPublishPanel',
    components: { SmallInfoBox, LabelField, EventCheckDialog },
    mixins: [GlobalsMixin, BackRouteMixin],

    props: {
        record: {
            type: Event,
            required: true,
        },
        // used to indicate form validity
        value: Boolean,
    },

    data() {
        return {
            showPublishConfirmDlg: false,
            showRejectConfirmDlg: false,
            showDraftConfirmDlg: false,
            showReasonDlg: false,
            eventCheckReasons: [],
            edited: false,
        };
    },

    computed: {
        userIsStaff,
        draftAllowed() {
            return this.record.status !== Event.STATUS_DRAFT;
        },
        rejectAllowed() {
            // const allowedStatus = [Event.STATUS_REGISTERED, Event.STATUS_RELEASED];
            // return allowedStatus.indexOf(this.record.status) >= 0;
            return this.record.status !== Event.STATUS_REJECTED;
        },
        publishAllowed() {
            // const allowedStatus = [Event.STATUS_DRAFT, Event.STATUS_REGISTERED, Event.STATUS_IMPORTED];
            // return allowedStatus.indexOf(this.record.status) >= 0;
            return this.record.status !== Event.STATUS_RELEASED;
        },
        infoboxesPublish() {
            return this.$store.state.banner.eventInfoboxes.collection
                ? this.$store.state.banner.eventInfoboxes.collection.models.filter(
                      (banner) => banner.type === 'infobox_event_publish',
                  )
                : [];
        },
        hidePreview() {
            return globalParam('event.hide_preview');
        },
    },

    created() {
        this.rules = formRules;
        this.loadEventInfoboxes();
    },

    methods: {
        ...mapActions('banner', ['loadEventInfoboxes']),

        onInput(field, value) {
            this.record.set(field, value);
        },

        getUrl(preview = false) {
            if (preview) {
                return this.record?.detailPreviewUrl;
            }
            return this.record?.detailUrl;
        },

        getUsageRightsUrl() {
            return globalParam('usage_rights_url');
        },

        getDataPolicyUrl() {
            return globalParam('data_policy_url');
        },

        async onPublishBtn(edited = false) {
            this.edited = edited;
            this.eventCheckReasons = [];
            let reasons = await this.checkEvent(Event.STATUS_RELEASED);
            if (reasons === false) {
                return;
            }
            if (reasons?.length === 0) {
                this.showPublishConfirmDlg = true;
            } else {
                // show reason dialog
                this.eventCheckReasons = reasons;
                this.showReasonDlg = true;
            }
        },

        /**
         * This method can be called when the event check was not successful,
         * but the (staff) user would publish the event anyway.
         * It initiates the publish method, even if the checks failed.
         */
        publishAnyway() {
            this.showReasonDlg = false;
            if (this.userIsStaff) {
                this.showPublishConfirmDlg = true;
            }
        },

        /**
         * Initiates a check if the event is ready to be published:
         * the backend checks the record for completeness, and returns
         * a list of errors if not.
         */
        async checkEvent(status) {
            try {
                this.$emit('saving', true);
                return this.record.checkStatusChange(status);
            } catch (e) {
                commitToast({ message: errToStr(e), color: 'error' });
                return false;
            } finally {
                this.$emit('saving', false);
            }
        },

        async doPublish() {
            await this.setStatus(Event.STATUS_RELEASED);
            commitToast({ message: this.$t('event_released'), color: 'success' });

            // route back to where we came from:
            if (this.getBackRouteConfig()?.route) {
                this.$router.push(this.getBackRouteConfig().route);
            } else {
                this.$router.push({ name: 'event' });
            }
        },

        onRejectBtn() {
            this.showRejectConfirmDlg = true;
        },

        async doReject() {
            await this.setStatus(Event.STATUS_REJECTED, false);
            commitToast({ message: this.$t('event_rejected'), color: 'success' });
        },

        async doRejectWithMail(message) {
            await this.setStatus(Event.STATUS_REJECTED, true, message);
            commitToast({ message: this.$t('event_rejected_with_mail'), color: 'success' });
        },

        onDraftBtn() {
            this.showDraftConfirmDlg = true;
        },

        async doDraft() {
            await this.setStatus(Event.STATUS_DRAFT);
            commitToast({ message: this.$t('event_draft'), color: 'success' });
        },
        async doDraftWithMail(message) {
            await this.setStatus(Event.STATUS_DRAFT, true, message);
            commitToast({ message: this.$t('event_drafted_with_mail'), color: 'success' });
        },

        async setStatus(newStatus, sendMail = true, message = null) {
            try {
                this.$emit('saving', true);
                await api().post('/Event/status', {
                    event_id: this.record.id,
                    status: newStatus,
                    sendMail,
                    message,
                    edited: this.edited,
                });
                this.$emit('reload-event');
            } catch (e) {
                commitToast({ message: errToStr(e), color: 'error' });
            } finally {
                this.$emit('saving', false);
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.main-container {
    display: grid;
    grid-template-areas: 'edit info';
    grid-template-rows: auto;
    grid-template-columns: minmax(400px, 800px) minmax(300px, 1fr);
    overflow: auto;
}
.publish-panel {
    grid-area: edit;
}
.info-panel {
    grid-area: info;
    background-color: var(--toolbar-bg-color);
}
</style>
